import React from 'react';
import IconButton from "@mui/material/IconButton";
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import VideocamIcon from '@mui/icons-material/Videocam';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import SearchIcon from "@mui/icons-material/Search";
import WorkHistoryIcon from "@mui/icons-material/WorkHistory";
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import SelectedCharacteristics from "../characteristics/SelectedCharcteristics";
import UnifiedAppBar from "../../components/navigation/UnifiedAppBar";
import { useHasActiveSubscription } from "../../hooks/useSubscriptionStatus";
import { canViewAdminTools } from "../../services/permissions";

/**
 * SearchAppBar - Top navigation bar for search mode
 * Uses UnifiedAppBar with search-specific left-side content
 *
 * @param {boolean} drawerOpen - Whether the filter drawer is open
 * @param {Function} onHistoryOpen - Handler for opening history drawer
 * @param {Function} onCameraOpen - Handler for opening camera modal
 * @param {Function} onVideoRecorderOpen - Handler for opening video recorder modal
 * @param {Function} onStealthOpen - Handler for opening stealth camera
 * @param {Function} onDrawerOpen - Handler for opening filter drawer
 * @param {Function} onSettingsOpen - Handler for opening settings
 * @param {Function} onAdminOpen - Handler for opening admin modal
 * @param {Function} onSubscriptionOpen - Handler for opening subscription dialog
 * @param {Object} cognitoUser - Current authenticated user
 * @param {boolean} stealthMode - Whether stealth mode is enabled
 * @param {boolean} videoEnabled - Whether stealth video capture is enabled
 * @param {Array} myarr - Selected characteristics array
 * @param {boolean} isAdminMode - Whether rendering in admin mode (hides camera/history)
 */
export default function SearchAppBar({
  drawerOpen,
  onHistoryOpen,
  onCameraOpen,
  onVideoRecorderOpen,
  onStealthOpen,
  onDrawerOpen,
  onSettingsOpen,
  onAdminOpen,
  onSubscriptionOpen,
  cognitoUser,
  stealthMode,
  videoEnabled,
  myarr,
  isAdminMode = false,
  cameraOpen = false,
  onPhotoLibraryClick
}) {
  const isUserActive = useHasActiveSubscription();
  const canAccessAdmin = canViewAdminTools(cognitoUser);

  // Build left-side content for search mode
  const leftContent = (
    <>
      {!isAdminMode && (
        <>
          <IconButton
            color="inherit"
            aria-label="history"
            onClick={onHistoryOpen}
            edge="start"
            sx={{ mr: 2, ...(drawerOpen && { display: "none" }) }}
          >
            <WorkHistoryIcon />
          </IconButton>

          <IconButton
            aria-label="camera"
            color="inherit"
            onClick={(e) => {
              e.stopPropagation();
              onCameraOpen();
            }}
            disabled={!isUserActive}
            title="Take Photo"
            sx={{
              ...(cameraOpen && {
                bgcolor: 'action.selected',
                '&:hover': { bgcolor: 'action.hover' }
              })
            }}
          >
            <CameraAltIcon />
          </IconButton>

          <IconButton
            aria-label="photo library"
            color="inherit"
            onClick={onPhotoLibraryClick}
            disabled={!isUserActive || !cameraOpen}
            title="Upload from Photo Library"
          >
            <PhotoLibraryIcon />
          </IconButton>

          <IconButton
            aria-label="video recorder"
            color="inherit"
            onClick={onVideoRecorderOpen}
            disabled={!isUserActive || !videoEnabled}
            title="Stealth Video Capture"
          >
            <VideocamIcon />
          </IconButton>

          <IconButton
            aria-label="stealth camera"
            color="inherit"
            onClick={onStealthOpen}
            disabled={!isUserActive || !stealthMode}
            title="Stealth Mode"
          >
            <VisibilityOffIcon />
          </IconButton>
        </>
      )}

      <IconButton
        color="inherit"
        aria-label="open drawer"
        onClick={onDrawerOpen}
        sx={{ mr: 2, ...(drawerOpen && { display: "none" }) }}
      >
        <SearchIcon />
      </IconButton>

      {canAccessAdmin && !isAdminMode && (
        <IconButton
          aria-label="admin"
          color="inherit"
          onClick={onAdminOpen}
          title="Administration"
        >
          <AdminPanelSettingsIcon />
        </IconButton>
      )}
    </>
  );

  // Build center content (selected characteristics)
  const centerContent = <SelectedCharacteristics myarr={myarr} />;

  return (
    <UnifiedAppBar
      mode="search"
      leftContent={leftContent}
      centerContent={centerContent}
      onSettingsOpen={onSettingsOpen}
      onSubscriptionOpen={onSubscriptionOpen}
      cognitoUser={cognitoUser}
      drawerOpen={drawerOpen}
    />
  );
}
